import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';
import { getMainImage } from '../../../utils/image-utils';
import ProductForm from './ProductForm';
import AnimatedList from '../../shared/components/AnimatedList';
import AnimatedItem from '../../shared/components/AnimatedItem';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface Product {
  id: number;
  name: string;
  price: number;
  stock: number;
  active: boolean;
  images?: string[];
}

function Produtos() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useNotification();

  // Verificar se há parâmetros de edição/criação
  const isEdit = searchParams.get('edit') !== null;
  const isNew = searchParams.get('new') === 'true';

  useEffect(() => {
    // Só carregar produtos se não estiver editando/criando
    if (!isEdit && !isNew) {
      loadProducts();
    }
  }, [isEdit, isNew]);

  const loadProducts = async () => {
    try {
      const productsData = await AdminAPI.get('products') as Product[];
      setProducts(productsData);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    if (filter === 'active' && !product.active) return false;
    if (filter === 'inactive' && product.active) return false;
    if (searchQuery && !product.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleEdit = (productId: number) => {
    navigate(`/admin/produtos?edit=${productId}`, { replace: false });
  };

  const handleDelete = async (productId: number) => {
    if (!confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await AdminAPI.delete(`products/${productId}`);
      success('Produto excluído com sucesso!');
      await loadProducts();
    } catch (err: any) {
      console.error('Erro ao excluir produto:', err);
      error(err.message || 'Erro ao excluir produto');
    }
  };

  // Se estiver editando ou criando, mostrar o formulário (após todos os hooks)
  if (isEdit || isNew) {
    return (
      <div className="form-transition form-transition-entered">
        <ProductForm />
      </div>
    );
  }

  return (
    <>
      {/* Loading Spinner - aparece apenas durante carregamento inicial */}
      {loading && <LoadingSpinner message="Carregando produtos..." />}
      
      {/* Filters Section */}
      <section className="filters-section">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar produtos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Ativos
          </button>
          <button
            className={`filter-btn ${filter === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Inativos
          </button>
        </div>
      </section>

      {/* Products List */}
      <section className="products-section">
        <AnimatedList
          listKey={`${filter}-${searchQuery}`}
          className="products-list"
        >
          {filteredProducts.length === 0 ? (
            <div>Nenhum produto encontrado</div>
          ) : (
            filteredProducts.map((product, index) => {
              const image = getMainImage(product.images);

              return (
                <AnimatedItem
                  key={product.id}
                  className="product-item"
                  index={index}
                >
                  <img
                    src={image}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/images/placeholder.jpg';
                    }}
                  />
                  <div className="product-info">
                    <div className="product-name">{product.name}</div>
                    <div className="product-details">
                      <span className="product-price">
                        R$ {parseFloat(String(product.price)).toFixed(2).replace('.', ',')}
                      </span>
                      <span className="product-stock">Estoque: {product.stock || 0}</span>
                    </div>
                    <div className={`product-status ${product.active ? 'active' : 'inactive'}`}>
                      {product.active ? 'Ativo' : 'Inativo'}
                    </div>
                  </div>
                  <div className="product-actions">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(product.id)}
                      title="Editar"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(product.id)}
                      title="Excluir"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </AnimatedItem>
              );
            })
          )}
        </AnimatedList>
      </section>
    </>
  );
}

export default Produtos;

